import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import throttle from 'lodash.throttle';

import { pvInfluencerCollections } from 'events/influencerhub';
import { MartyContext } from 'utils/context';
import { onEvent } from 'helpers/EventHelpers';
import { titaniteView, track } from 'apis/amethyst';
import { offset } from 'helpers/ClientUtils';
import { pvCollections } from 'events/favorites';
import { pageTypeChange } from 'actions/common';
import { firePixelServer } from 'actions/pixelServer';
import { clearHeartList, fetchCollectionList, getHearts, getLists, toggleInfluencerCollectionVisibility } from 'actions/hearts';
import { redirectTo, redirectToAuthenticationFor } from 'actions/redirect';
import { makeCollectionInformationWithItems } from 'helpers/HeartUtils';
import { PageLoader } from 'components/Loader';
import { Favorites } from 'containers/account/Favorites';
import { InfluencerCollections } from 'components/influencer/collections/InfluencerCollections';
import { createNewCollection, setShoppablePostCreationSource, updateInfluencerCollectionBanner } from 'actions/influencer/influencer';

// used on the old favorites page
const LAZYLOAD_HEARTS_OFFSET = 500;
const NEW_COLLECTION_PAGE = '/influencer/hub/new/collection';

export class Collections extends Component {
  constructor(props) {
    super(props);
    this.loadMore = createRef();
    this.onScrollPage = throttle(this.onScrollPage.bind(this), 100);
  }

  state = {
    showNewCollectionModal: false,
    showShareCollectionModal: false,
    showDeleteCollectionModal: false,
    currentlyFocusedListId: null
  };

  componentDidMount() {
    const { isCustomer, clearHeartList, redirectToAuthenticationFor, router } = this.props;
    const {
      marketplace: {
        account: { simpleCollections }
      }
    } = this.context;

    // immediately navigate you to your default heart list if Simple Collections is enabled and you're logged in
    if (isCustomer && simpleCollections) {
      router.replace('/account/favorites/h.');
      return;
    }

    titaniteView();
    if (isCustomer) {
      clearHeartList();
      this.fetchData();
      this.setupScrollHandler();
    } else {
      redirectToAuthenticationFor(window.location, '/');
    }
  }

  setupScrollHandler = () => {
    this.scrollHandler = onEvent(window, 'scroll', this.onScrollPage, null, this);
  };

  onScrollPage = () => {
    const { scrollY, pageYOffset, innerHeight } = window;
    const loadMore = this.loadMore.current;

    if (!loadMore) {
      this.scrollHandler.unbind();
      this.scrollHandler = null; // remove the handler event so the other methods know to resetup
      return;
    }

    const {
      getLists,
      hearts: { collectionNextPageToken }
    } = this.props;
    const offsetScroll = (scrollY || pageYOffset) + innerHeight + LAZYLOAD_HEARTS_OFFSET;
    if (offsetScroll > offset(loadMore).top) {
      // unbind scroll while we fetch lists then hearts);
      this.scrollHandler.unbind();
      getLists({ nextPageToken: collectionNextPageToken, concat: true }).then(() => {
        // rebind scroll handler and get lists data after finishing the list call
        this.fetchListData();
        if (loadMore) {
          this.setupScrollHandler();
        }
      });
    }
  };

  firePageView = () => {
    const {
      hearts: { lists }
    } = this.props;
    const {
      marketplace: { isInfluencerProgramEnabled }
    } = this.context;
    const trackCollections = isInfluencerProgramEnabled ? pvInfluencerCollections : pvCollections;
    track(() => [
      trackCollections,
      {
        collectionCount: lists.length
      }
    ]);
  };

  fetchListData = () => {
    const {
      hearts: { lists },
      fetchCollectionList
    } = this.props;
    lists?.forEach(({ listId }) => {
      fetchCollectionList({ listId });
    });
  };

  getListsCallback = () => {
    this.fetchListData();
    this.firePageView();
  };

  fetchData = () => {
    const {
      location: { search },
      getLists,
      getHearts
    } = this.props;

    const searchParams = new URLSearchParams(search);
    const heartOnLoad = searchParams.get('heartOnLoad');
    const heartOnLoadSub = searchParams.get('heartOnLoadSub');

    if (heartOnLoad || heartOnLoadSub) {
      getHearts().then(getLists).then(this.getListsCallback);
    } else {
      getLists().then(this.getListsCallback);
    }
  };

  markInfluencerCollection = listId => {
    const { toggleInfluencerCollectionVisibility } = this.props;
    toggleInfluencerCollectionVisibility({ listId, toPublic: true });
  };

  onStartNewCollection = e => {
    e.preventDefault();
    const { createNewCollection, redirectTo } = this.props;
    createNewCollection();
    redirectTo(NEW_COLLECTION_PAGE);
  };

  onStartNewList = e => {
    e.preventDefault();
    this.setState({ showNewCollectionModal: true });
  };

  onSaveNewCollection = () => {
    const { getLists } = this.props;
    this.setState({ showNewCollectionModal: false });
    getLists().then(this.fetchListData);

    // upon deletion or save there is a refetch and if you hit the bottom
    // the handler doesnt get rebinded
    if (!this.scrollHandler) {
      this.setupScrollHandler();
    }
  };

  onCancelNewCollection = () => {
    this.setState({ showNewCollectionModal: false });
  };

  onDeleteCollection = () => {
    const { getLists } = this.props;
    this.setState({
      showDeleteCollectionModal: false,
      currentlyFocusedListId: null
    });
    getLists().then(this.fetchListData);

    // upon deletion or save there is a refetch and if you hit the bottom
    // the handler doesnt get rebinded
    if (!this.scrollHandler) {
      this.setupScrollHandler();
    }
  };

  onShareCollection = () => {
    this.setState({
      showShareCollectionModal: false,
      currentlyFocusedListId: null
    });
    this.fetchData();
  };

  onOpenShareModal = e => {
    e.preventDefault();
    const listId = e.target.dataset.listid;
    this.setState({
      showShareCollectionModal: true,
      currentlyFocusedListId: listId
    });
  };

  onOpenDeleteModal = e => {
    e.preventDefault();
    const listId = e.target.dataset.listid;
    this.setState({
      showDeleteCollectionModal: true,
      currentlyFocusedListId: listId
    });
  };

  render() {
    return (
      <MartyContext.Consumer>
        {context => {
          this.context = context;
          const {
            pageType,
            isInfluencer,
            isCustomer,
            hearts: { lists, collections, collectionNextPageToken, heartsStyleIds = [] },
            setShoppablePostCreationSource,
            showCollectionBanner,
            updateInfluencerCollectionBanner
          } = this.props;
          const { showNewCollectionModal, showDeleteCollectionModal, showShareCollectionModal, currentlyFocusedListId } = this.state;
          const collectionWithItems = makeCollectionInformationWithItems(lists, collections);
          const {
            testId,
            marketplace: { isInfluencerProgramEnabled }
          } = context;

          if (!isCustomer || !collectionWithItems.length) {
            return <PageLoader centered={true} />;
          }
          const isInfluencerAccount = isInfluencerProgramEnabled && isInfluencer;
          const collectionProps = {
            testId,
            currentlyFocusedListId,
            collectionWithItems,
            collectionNextPageToken,
            showNewCollectionModal,
            onCancelNewCollection: this.onCancelNewCollection,
            onSaveNewCollection: this.onSaveNewCollection,
            showDeleteCollectionModal,
            showShareCollectionModal,
            onShareCollection: this.onShareCollection,
            onDeleteCollection: this.onDeleteCollection,
            onStartNewCollection: this.onStartNewCollection,
            loadMore: this.loadMore,
            onOpenShareModal: this.onOpenShareModal,
            onOpenDeleteModal: this.onOpenDeleteModal,
            setShoppablePostCreationSource,
            updateInfluencerCollectionBanner,
            showCollectionBanner
          };
          if (pageType === 'influencerhub') {
            return <InfluencerCollections {...collectionProps} />;
          }
          const listProps = {
            ...collectionProps,
            onStartNewCollection: this.onStartNewList,
            isInfluencerAccount,
            heartsStyleIds
          };
          return <Favorites {...listProps} />;
        }}
      </MartyContext.Consumer>
    );
  }
}

export const mapStateToProps = state => ({
  isCustomer: !!state.cookies['x-main'],
  hearts: state.hearts,
  location: state.router.location,
  isInfluencer: state.influencer.isInfluencer,
  pageType: state.pageView.pageType
});

export default connect(mapStateToProps, {
  redirectToAuthenticationFor,
  fetchCollectionList,
  clearHeartList,
  getLists,
  firePixelServer,
  pageTypeChange,
  getHearts,
  toggleInfluencerCollectionVisibility,
  updateInfluencerCollectionBanner,
  setShoppablePostCreationSource,
  createNewCollection,
  redirectTo
})(Collections);
